// Form fields that render as inputs on a draft and as plain text otherwise
// (in-review / published versions are frozen, so reviewers read, not edit).

import { useId, useState } from "react";
import { parseList, wordCount } from "./format";

interface BaseProps {
  label: string;
  editable: boolean;
}

function ReadOnly({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="tt-label">{label}</span>
      <div className="tt-ro">{children}</div>
    </div>
  );
}

// Spoken-length hint next to voice fields ("12 / 15 words").
function WordHint({ text, max }: { text: string; max: number }) {
  const n = wordCount(text);
  if (!n) return null;
  return (
    <span className={`tt-words${n > max ? " over" : ""}`}>
      {" "}· {n} / {max} words
    </span>
  );
}

export function TextField({
  label,
  editable,
  value,
  onChange,
  placeholder,
  multiline,
  rows = 2,
  maxWords,
}: BaseProps & {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  // Spoken fields: show a word count against this limit.
  maxWords?: number;
}) {
  const id = useId();
  if (!editable) {
    return (
      <ReadOnly label={label}>
        {value || <span className="muted">—</span>}
        {maxWords && <WordHint text={value} max={maxWords} />}
      </ReadOnly>
    );
  }
  return (
    <div>
      <label htmlFor={id}>
        {label}
        {maxWords && <WordHint text={value} max={maxWords} />}
      </label>
      {multiline ? (
        <textarea id={id} rows={rows} value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} type="text" value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export function NumberField({
  label,
  editable,
  value,
  onChange,
  min,
  max,
}: BaseProps & { value: number; onChange: (value: number) => void; min?: number; max?: number }) {
  const id = useId();
  if (!editable) return <ReadOnly label={label}>{value}</ReadOnly>;
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <input id={id} type="number" min={min} max={max} value={value}
        onChange={(e) => onChange(parseInt(e.target.value || String(min ?? 0), 10))} />
    </div>
  );
}

export function SelectField({
  label,
  editable,
  value,
  options,
  onChange,
  emptyLabel,
}: BaseProps & {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  // When set, offers an empty choice with this label (e.g. "none").
  emptyLabel?: string;
}) {
  const id = useId();
  if (!editable) {
    return <ReadOnly label={label}>{value ? value.replaceAll("_", " ") : emptyLabel || "—"}</ReadOnly>;
  }
  // Keep an out-of-list value visible instead of silently re-mapping it.
  const all = value && !options.includes(value) ? [...options, value] : options;
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
        {all.map((o) => (
          <option key={o} value={o}>
            {o.replaceAll("_", " ")}{options.includes(o) ? "" : " (unknown)"}
          </option>
        ))}
      </select>
    </div>
  );
}

// One item per line. Keeps its own text so a trailing newline survives while
// typing; re-syncs when the list changes from outside (version switch, apply).
export function ListField({
  label,
  editable,
  value,
  onChange,
  rows = 3,
  placeholder,
}: BaseProps & {
  value: string[];
  onChange: (value: string[]) => void;
  rows?: number;
  placeholder?: string;
}) {
  const id = useId();
  const joined = value.join("\n");
  const [text, setText] = useState(joined);
  const [synced, setSynced] = useState(joined);
  if (joined !== synced) {
    setSynced(joined);
    if (parseList(text).join("\n") !== joined) setText(joined);
  }

  if (!editable) {
    return (
      <ReadOnly label={label}>
        {value.length === 0 ? (
          <span className="muted">—</span>
        ) : (
          <ul className="tt-ro-list">
            {value.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        )}
      </ReadOnly>
    );
  }
  return (
    <div>
      <label htmlFor={id}>{label} <span className="muted">(one per line)</span></label>
      <textarea id={id} rows={rows} value={text} placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseList(e.target.value));
        }} />
    </div>
  );
}
